module Sm.UI.Activity

open System
open System.Runtime.CompilerServices
open MobF

open Sm.Model.Record
open Sm.UI.InputParser

type StepStatus =
    | Pending 
    | Cancelled
    | Complete of TimeSpan option

type StepTime =
    {
        Estimate: TimeSpan
        Status: StepStatus
        SessionMarker: bool option
    }
    member t.Pending =
        match t.Status with
        | Pending -> true
        | _ -> false

type ActivityMsg =
    | CompleteStep of TimeSpan
    | ClearStep
    | MarkSession of bool

type ActivityComputed =
    abstract Syntax: SyntaxNode list
    abstract Tags: Set<string>
    abstract Time: StepTime option

module Accessor =
    let timer =
        let read = function
            | [Meta.Int t] -> Some (TimeSpan.FromSeconds(t))
            | _ -> None
        let write (value: TimeSpan) =
            let seconds = (round >> int) value.TotalSeconds
            [Meta.Int seconds]
        Meta.makeAccessor "timer" read write

    let sessionMarker =
        let read = function
            | [Meta.Bool value] -> Some value
            | _ -> None
        let write value =
            [Meta.Bool value]
        Meta.makeAccessor "session-marker" read write

type IComputed with
    member computed.Activity = computed.Extend(fun state ->
        Computation.structural { new ActivityComputed with
            member _.Syntax =
                parse state.Text

            member this.Tags =
                this.Syntax
                |> Seq.choose (function Tag t -> Some t | _ -> None)
                |> Set.ofSeq

            member this.Time =
                let timer =
                    this.Syntax
                    |> List.tryPick (function
                        | Timer x -> Some x
                        | _ -> None
                    )

                match timer with
                | None -> None
                | Some minutes ->
                    Some {
                        Estimate = TimeSpan.FromMinutes(minutes)
                        Status =
                            match Accessor.timer.Get state.Metadata with
                            | None when computed.ArchiveDate.IsSome -> Complete None
                            | None -> Pending
                            | Some t when t = TimeSpan.Zero -> Cancelled
                            | t -> Complete t
                        SessionMarker =
                            Accessor.sessionMarker.Get(state.Metadata)
                    }
        }
    )

[<Extension>]
type Extensions() =
    [<Extension>]
    static member Post(record: Type, msg: ActivityMsg) =
        match msg with
        | CompleteStep elapsed ->
            record.Post(Archive)
            record.Post(Accessor.timer.Set(elapsed))

        | ClearStep ->
            record.Post(Archive)
            record.Post(Accessor.timer.Set(TimeSpan.Zero))

        | MarkSession status ->
            record.Post(Accessor.sessionMarker.Set(status))
